import { clientPhotosConstants } from '../_constants/clientPhotos.constants';
import { clientPhotosService } from '../_services/clientPhotos.service';
import { normaliseImage } from '../component/shared/normaliseImage';

export const clientPhotosActions = {
    fetchPhotos,
    loadMore,
    uploadPhoto,
    patchPhotoTags,
    setShareConsent,
    deletePhoto,
    undoDelete,
    commitDelete,
    setConsent,
    revokeConsent,
    fetchSalonTags,
    fetchTagsForService,
};

function fetchPhotos(clientId) {
    return async (dispatch) => {
        dispatch({ type: clientPhotosConstants.LOAD_REQUEST, clientId, cursor: null });
        try {
            const { photos, nextCursor, consent } = await clientPhotosService.fetchClientPhotos(clientId);
            dispatch({
                type: clientPhotosConstants.LOAD_SUCCESS,
                clientId,
                photos,
                nextCursor,
                consent,
            });
        } catch (err) {
            dispatch({
                type: clientPhotosConstants.LOAD_FAILURE,
                clientId,
                error: err?.message || 'Failed to load photos',
            });
        }
    };
}

function loadMore(clientId) {
    return async (dispatch, getState) => {
        const state = getState().clientPhotos?.byClientId?.[clientId];
        if (!state?.nextCursor || state.loadingMore) return;
        dispatch({
            type: clientPhotosConstants.LOAD_REQUEST,
            clientId,
            cursor: state.nextCursor,
        });
        try {
            const { photos, nextCursor, consent } = await clientPhotosService.fetchClientPhotos(
                clientId,
                { cursor: state.nextCursor },
            );
            dispatch({
                type: clientPhotosConstants.LOAD_SUCCESS,
                clientId,
                photos,
                nextCursor,
                consent,
            });
        } catch (err) {
            dispatch({
                type: clientPhotosConstants.LOAD_FAILURE,
                clientId,
                error: err?.message || 'Failed to load more photos',
            });
        }
    };
}

function uploadPhoto(clientId, file, { bookingId = null, tags = [] } = {}) {
    return async (dispatch) => {
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const previewUrl = URL.createObjectURL(file);
        const optimistic = {
            tempId,
            clientId,
            bookingId,
            tags,
            capturedAt: new Date().toISOString(),
            thumbUrl: previewUrl,
            url: previewUrl,
            width: 0,
            height: 0,
            uploading: true,
        };
        dispatch({ type: clientPhotosConstants.UPLOAD_OPTIMISTIC, clientId, optimistic });

        let normalised;
        try {
            normalised = await normaliseImage(file);
        } catch {
            // Fall back to the raw file — the server can handle anything
            normalised = file;
        }

        try {
            const { photo } = await clientPhotosService.uploadClientPhoto(clientId, normalised, {
                bookingId,
                tags,
            });
            dispatch({
                type: clientPhotosConstants.UPLOAD_SUCCESS,
                clientId,
                tempId,
                photo,
            });
        } catch (err) {
            dispatch({
                type: clientPhotosConstants.UPLOAD_FAILURE,
                clientId,
                tempId,
                error: err?.message || 'Upload failed',
            });
        }
    };
}

function patchPhotoTags(clientId, photoId, tags, previousTags) {
    return async (dispatch) => {
        dispatch({ type: clientPhotosConstants.PATCH_OPTIMISTIC, clientId, photoId, tags });
        try {
            await clientPhotosService.patchClientPhoto(clientId, photoId, { tags });
            dispatch({ type: clientPhotosConstants.PATCH_SUCCESS, clientId, photoId });
            // Refresh salon tags so autocomplete reflects the latest vocabulary.
            dispatch(fetchSalonTags());
        } catch (err) {
            dispatch({
                type: clientPhotosConstants.PATCH_FAILURE,
                clientId,
                photoId,
                previousTags,
                error: err?.message || 'Failed to update tags',
            });
        }
    };
}

// value: 'none' | 'noFace' | 'social'. Who and when are recorded by the
// server; `previous` is the photo as it was, restored if the save fails.
function setShareConsent(clientId, photoId, value, previous) {
    return async (dispatch) => {
        dispatch({
            type: clientPhotosConstants.SHARE_CONSENT_OPTIMISTIC,
            clientId,
            photoId,
            patch: { shareConsent: value, shareConsentAt: new Date().toISOString(), shareConsentEffective: value },
        });
        try {
            const { photo } = await clientPhotosService.setShareConsent(photoId, value);
            dispatch({
                type: clientPhotosConstants.SHARE_CONSENT_SUCCESS,
                clientId,
                photoId,
                patch: {
                    shareConsent: photo.shareConsent,
                    shareConsentAt: photo.shareConsentAt,
                    shareConsentBy: photo.shareConsentBy,
                    shareConsentEffective: photo.shareConsentEffective,
                    shareConsentAudit: photo.shareConsentAudit,
                },
            });
            return true;
        } catch (err) {
            const prev = previous || {};
            dispatch({
                type: clientPhotosConstants.SHARE_CONSENT_FAILURE,
                clientId,
                photoId,
                patch: {
                    shareConsent: prev.shareConsent,
                    shareConsentAt: prev.shareConsentAt || null,
                    shareConsentEffective: prev.shareConsentEffective,
                },
                error: err?.message || 'Failed to save sharing permission',
            });
            return false;
        }
    };
}

function fetchSalonTags() {
    return async (dispatch) => {
        try {
            const { tags } = await clientPhotosService.fetchSalonTags();
            dispatch({ type: clientPhotosConstants.SALON_TAGS_LOAD_SUCCESS, tags });
        } catch {
            /* silent — picker will just show suggestions only */
        }
    };
}

function fetchTagsForService(serviceName) {
    return async (dispatch, getState) => {
        if (!serviceName) return;
        // Avoid refetching a service we've already loaded this session.
        const cached = getState().clientPhotos?.contextTagsByService?.[serviceName];
        if (cached) return;
        try {
            const { tags } = await clientPhotosService.fetchTagsForService(serviceName);
            dispatch({
                type: clientPhotosConstants.CONTEXT_TAGS_LOAD_SUCCESS,
                service: serviceName,
                tags,
            });
        } catch {
            /* silent */
        }
    };
}

function deletePhoto(clientId, photoId) {
    return (dispatch) => {
        dispatch({ type: clientPhotosConstants.DELETE_OPTIMISTIC, clientId, photoId });
    };
}

function undoDelete(clientId, photoId) {
    return (dispatch) => {
        dispatch({ type: clientPhotosConstants.DELETE_UNDO, clientId, photoId });
    };
}

function commitDelete(clientId, photoId) {
    return async (dispatch) => {
        try {
            await clientPhotosService.deleteClientPhoto(clientId, photoId);
            dispatch({ type: clientPhotosConstants.DELETE_COMMIT, clientId, photoId });
        } catch (err) {
            dispatch({
                type: clientPhotosConstants.DELETE_FAILURE,
                clientId,
                photoId,
                error: err?.message || 'Failed to delete photo',
            });
        }
    };
}

function setConsent(clientId) {
    return async (dispatch) => {
        try {
            const { consent } = await clientPhotosService.setPhotoConsent(clientId);
            dispatch({ type: clientPhotosConstants.CONSENT_SET, clientId, consent });
            return consent;
        } catch {
            return null;
        }
    };
}

function revokeConsent(clientId) {
    return async (dispatch) => {
        try {
            await clientPhotosService.revokePhotoConsent(clientId);
            dispatch({ type: clientPhotosConstants.CONSENT_REVOKE, clientId });
            return true;
        } catch {
            return false;
        }
    };
}
